export const CHECKOUT_FALLBACK_PATH = '/api/checkout';

// Read by apps/cms/src/lib/posthog-shopify-purchase.ts and
// meta-shopify-purchase.ts from the order's note_attributes — the literals
// are pinned by tests in both apps so they cannot drift apart.
export const POSTHOG_CHECKOUT_ATTRIBUTE_KEYS = {
	distinctId: 'posthog_distinct_id',
	sessionId: 'posthog_session_id',
	featureFlags: 'posthog_feature_flags',
} as const;

export const META_CHECKOUT_ATTRIBUTE_KEYS = {
	fbp: '_meta_fbp',
	fbc: '_meta_fbc',
	externalId: '_meta_external_id',
	sourceUrl: '_meta_source_url',
	userAgent: '_meta_user_agent',
	marketingConsent: '_meta_marketing_consent',
} as const;

// Mark's first-party visitor id, set by mark.js on this domain. Shopify
// surfaces a cart attribute on the order as a note attribute, which is the
// only bridge between the journey recorded here and the order placed on
// Shopify's checkout — the web pixel there mints its own id that has no
// history behind it.
export const MARK_CHECKOUT_ATTRIBUTE_KEY = '_mkpx';

export const META_MARKETING_CONSENT_VALUES = {
	granted: 'granted',
	denied: 'denied',
} as const;

// Short-lived first-party cookies the client sets on checkout click so the
// /api/checkout server fallback can re-attach the same consent-gated PostHog
// identity. Without them, the fallback's replace-all cartAttributesUpdate can
// race a late-landing client write and erase the order's PostHog stitching.
export const POSTHOG_HANDOFF_COOKIE_NAMES = {
	distinctId: 'wm_ph_did',
	sessionId: 'wm_ph_sid',
} as const;
export const POSTHOG_HANDOFF_COOKIE_MAX_AGE_SECONDS = 600;

// Carries the already-resolved storefront privacy decision to /api/checkout.
// It is a short-lived functional handoff value, not a marketing identifier.
export const META_CONSENT_HANDOFF_COOKIE_NAME = 'wm_meta_consent';
export const META_CONSENT_HANDOFF_COOKIE_MAX_AGE_SECONDS = 600;

// The CMS Purchase reader discards source URLs over 2048 chars, and all
// attribution rides ONE atomic cartAttributesUpdate — a single over-limit
// value (utm-laden URL, huge flag map) would reject the whole mutation and
// lose every identifier with it, so values are capped before the write.
export const CHECKOUT_SOURCE_URL_MAX_LENGTH = 2_048;
export const CHECKOUT_FEATURE_FLAGS_MAX_LENGTH = 4_096;

export function capCheckoutSourceUrl(href: string | null | undefined) {
	if (!href) return '';
	if (href.length <= CHECKOUT_SOURCE_URL_MAX_LENGTH) return href;

	try {
		const url = new URL(href);
		const withoutQuery = `${url.origin}${url.pathname}`;
		return withoutQuery.length <= CHECKOUT_SOURCE_URL_MAX_LENGTH ? withoutQuery : url.origin;
	} catch {
		return '';
	}
}

export function capCheckoutFeatureFlags(json: string) {
	return json.length <= CHECKOUT_FEATURE_FLAGS_MAX_LENGTH ? json : '';
}

export function normalizePostHogHandoffValue(value: string | null | undefined) {
	const trimmed = value?.trim();
	// Printable ASCII with no spaces covers PostHog UUIDs and identified ids
	// while rejecting anything that could corrupt a cart attribute.
	return trimmed && trimmed.length <= 200 && /^[\x21-\x7e]+$/.test(trimmed) ? trimmed : '';
}

export type CheckoutAttribute = { key: string; value: string };

export interface CheckoutHandoffContext {
	fbp?: string | null;
	fbc?: string | null;
	externalId?: string | null;
	marketingConsent?: string | null;
	posthogDistinctId?: string | null;
	posthogSessionId?: string | null;
	markVisitorId?: string | null;
	referrer?: string | null;
	requestUrl: string;
	userAgent?: string | null;
}

export function buildServerCheckoutAttributes(
	context: CheckoutHandoffContext,
): CheckoutAttribute[] {
	// The consent marker remains diagnostic and still controls browser event
	// delivery. Meta Purchase attribution identifiers are attached regardless.
	const marketingConsent = normalizeMetaMarketingConsent(context.marketingConsent);
	const fbp = normalizeMetaBrowserId(context.fbp);
	const externalId = normalizeMetaExternalId(context.externalId);
	// Cookie-carried fbc only. Reconstructing one from a referrer fbclid would
	// stamp a send-time timestamp on the click — the same manufactured-signal
	// pattern the CMS Purchase reader refuses for landing_site fbclids. The
	// _fbc cookie minted at landing carries the real click time.
	const fbc = normalizeMetaBrowserId(context.fbc);

	return [
		{
			key: MARK_CHECKOUT_ATTRIBUTE_KEY,
			value: normalizeMarkVisitorId(context.markVisitorId),
		},
		{
			key: META_CHECKOUT_ATTRIBUTE_KEYS.fbp,
			value: fbp,
		},
		{
			key: META_CHECKOUT_ATTRIBUTE_KEYS.fbc,
			value: fbc,
		},
		{
			key: META_CHECKOUT_ATTRIBUTE_KEYS.externalId,
			value: externalId,
		},
		{
			key: META_CHECKOUT_ATTRIBUTE_KEYS.sourceUrl,
			value: getSafeCheckoutSourceUrl(context.referrer, context.requestUrl),
		},
		{
			key: META_CHECKOUT_ATTRIBUTE_KEYS.userAgent,
			value: context.userAgent?.slice(0, 500) ?? '',
		},
		{
			key: META_CHECKOUT_ATTRIBUTE_KEYS.marketingConsent,
			value: marketingConsent,
		},
		{
			key: POSTHOG_CHECKOUT_ATTRIBUTE_KEYS.distinctId,
			value: normalizePostHogHandoffValue(context.posthogDistinctId),
		},
		{
			key: POSTHOG_CHECKOUT_ATTRIBUTE_KEYS.sessionId,
			value: normalizePostHogHandoffValue(context.posthogSessionId),
		},
	];
}

// The server-side handoff only sees cookies — never the client's localStorage
// click-id stash — so its empty values prove nothing and must not blank
// identifiers the client already attached to the cart.
export function dropEmptyCheckoutAttributes(attributes: CheckoutAttribute[]) {
	return attributes.filter((attribute) => attribute.value !== '');
}

const META_TRACKING_ATTRIBUTE_KEYS = new Set<string>(Object.values(META_CHECKOUT_ATTRIBUTE_KEYS));

// An empty PostHog value is authoritative and clears an older cart/session
// identity. Empty Meta identifiers are inconclusive because browser cookie
// expiry can precede the click-attribution window, so keep an earlier cart
// value unless a new identifier is available.
export function prepareClientCheckoutAttributes(attributes: CheckoutAttribute[]) {
	return attributes.filter(
		(attribute) => attribute.value !== '' || !META_TRACKING_ATTRIBUTE_KEYS.has(attribute.key),
	);
}

export function mergeCheckoutAttributes(
	currentAttributes: CheckoutAttribute[] | null | undefined,
	nextAttributes: CheckoutAttribute[],
) {
	const merged = new Map(
		(currentAttributes ?? []).map((attribute) => [attribute.key, attribute.value]),
	);

	for (const attribute of nextAttributes) {
		// Meta attribution is append/update-only. Missing browser state, consent
		// denial, cookie expiry, or a fallback race must never erase a value that
		// already made it onto the cart. Non-empty values may still supersede an
		// older click after a new ad landing.
		if (
			attribute.value === '' &&
			META_TRACKING_ATTRIBUTE_KEYS.has(attribute.key) &&
			merged.has(attribute.key)
		) {
			continue;
		}
		merged.set(attribute.key, attribute.value);
	}

	return Array.from(merged, ([key, value]) => ({ key, value }));
}

function normalizeMetaBrowserId(value: string | null | undefined) {
	const trimmed = value?.trim();
	return trimmed && /^fb\.\d\.\d+\.\S{1,450}$/.test(trimmed) ? trimmed : '';
}

export function normalizeMarkVisitorId(value: string | null | undefined) {
	const trimmed = value?.trim();
	// mark.js writes a uuid or a "v-<base36>-<base36>" fallback; accept either
	// shape and nothing that could corrupt a cart attribute.
	return trimmed && trimmed.length <= 200 && /^[\x21-\x7e]+$/.test(trimmed) ? trimmed : '';
}

function normalizeMetaExternalId(value: string | null | undefined) {
	const trimmed = value?.trim();
	return trimmed && /^[a-zA-Z0-9][a-zA-Z0-9._:-]{7,255}$/.test(trimmed) ? trimmed : '';
}

function normalizeMetaMarketingConsent(value: string | null | undefined) {
	return value === META_MARKETING_CONSENT_VALUES.granted
		? META_MARKETING_CONSENT_VALUES.granted
		: META_MARKETING_CONSENT_VALUES.denied;
}

function getSafeCheckoutSourceUrl(referrer: string | null | undefined, requestUrl: string) {
	try {
		const request = new URL(requestUrl);
		const source = referrer ? new URL(referrer) : null;
		if (
			source &&
			source.origin === request.origin &&
			source.href.length <= CHECKOUT_SOURCE_URL_MAX_LENGTH &&
			(source.protocol === 'https:' || source.protocol === 'http:')
		) {
			return source.href;
		}

		return new URL('/cart', request).href;
	} catch {
		return '';
	}
}
